import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Users, Upload, BarChart3, Settings, Calendar, FileText,
  MessageSquare, Shield, CheckCircle, ArrowRight, BookOpen,
  Target, Bell, Zap, Brain
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SiteNav, SiteFooter } from './HomePage';

const SECTIONS = [
  {
    id: 'overview', title: 'Your Admin Dashboard', icon: BarChart3, color: '#E86C3D',
    description: 'The central hub for managing your entire program.',
    content: 'Your dashboard gives you a bird\'s-eye view of everything: total youth, goals completed, courses done, consent status, and more — all updated in real time. Click any KPI card to drill into the details.',
    features: ['8 clickable KPI cards with live data', 'AI Insights analyzing your program health', 'Recent Activity feed showing what youth are doing', 'Program Health cards (Active, Inactive, Staff, Parents)']
  },
  {
    id: 'users', title: 'User Management', icon: Users, color: '#3B82F6',
    description: 'Add, remove, and manage all participants and staff.',
    content: 'Upload youth and staff via CSV (hundreds at a time), or add individuals with the "Add User" button. Assign youth to lead monitors, suspend accounts, and track who\'s active.',
    features: ['CSV bulk import (staff first, then youth with auto-assignment)', '"Add User" button for individual additions', 'Suspend/reactivate accounts', 'Search and filter by role, status, cohort']
  },
  {
    id: 'faq', title: 'FAQ Management', icon: FileText, color: '#8B5CF6',
    description: 'Build an FAQ that reduces the questions your staff has to answer.',
    content: 'Add FAQs one at a time or bulk upload a document of Q&A pairs. When your lead monitors answer youth questions, they can promote great answers to FAQ with one click.',
    features: ['Add individually or bulk upload', 'Promote from Q&A with one click', 'Youth see FAQs in their Inbox → FAQ tab', 'Scoped per organization — other clients never see yours']
  },
  {
    id: 'events', title: 'Events & Orientation', icon: Calendar, color: '#10B981',
    description: 'Create events using templates, manage schedules, and track attendance.',
    content: 'Choose from 5 event templates (Orientation, Workshop, Speaker, Milestone, Celebration) or create custom. Build multi-day schedules with sessions. Youth check in per session and complete post-event surveys.',
    features: ['5 event templates with pre-filled settings', 'Multi-day schedules with session check-in', 'Post-event surveys auto-triggered', 'Virtual event links + "Add to Calendar" for youth']
  },
  {
    id: 'surveys', title: 'Surveys', icon: Target, color: '#F59E0B',
    description: 'Create surveys anytime — built-in questions or link to external tools.',
    content: 'Build quick surveys with rating and text questions directly in the platform, or link to Typeform/Google Forms for complex assessments. View completion rates and individual responses.',
    features: ['Built-in survey builder (rating + text questions)', 'External link support (Typeform, Google Forms)', '"Required" toggle forces completion', 'Real-time results with completion rates']
  },
  {
    id: 'resources', title: 'Documents & Resources', icon: Upload, color: '#EC4899',
    description: 'Upload files and links that youth can access anytime.',
    content: 'Upload PDFs, images (max 10MB) or add external links. Organize by category, feature important docs at the top. Youth are automatically notified when you upload something new.',
    features: ['File upload (PDF, images) + external links', 'Categories: orientation, career, financial, safety, forms', '"Featured" toggle pins important docs to top', 'Youth get notified automatically on new uploads']
  },
  {
    id: 'assessments', title: 'Assessment System', icon: Brain, color: '#EF4444',
    description: 'Push assessments to youth, track completion, and download results.',
    content: 'Push mandatory pre-program and post-program assessments to all youth. Track completion rates, view individual scores, download CSV for your data team. Youth get AI-powered feedback on their results.',
    features: ['Push mandatory assessments with one click', 'Track completion rates per cohort', 'CSV export for all assessment data', '6-domain scoring with proficiency bands']
  },
  {
    id: 'reports', title: 'Reports & Export', icon: BarChart3, color: '#2EC4B6',
    description: 'Export data, generate AI reports, and track program impact.',
    content: 'Export all youth data as CSV for funder reports. Generate individual AI Profile Reports for any youth. View program-wide analytics including goal completion rates, course engagement, and assessment growth.',
    features: ['One-click CSV export of all youth data', 'AI Profile Reports per youth', 'Program-wide analytics dashboard', 'Assessment growth tracking (pre vs post)']
  },
  {
    id: 'settings', title: 'Program Settings', icon: Settings, color: '#6B7280',
    description: 'Configure your program name, dates, cohorts, and more.',
    content: 'Edit program name, description, dates, location, and cohort names. All changes are visible to your users immediately.',
    features: ['Edit program name and description', 'Set start/end dates', 'Manage cohort/group names', 'Staff role label (Lead Monitor, Counselor, etc.)']
  },
];

const AdminTutorialPage = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSection(id);
  };

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => { if (entry.isIntersecting) setActiveSection(entry.target.id); });
    }, { threshold: 0.3 });
    SECTIONS.forEach(s => { const el = document.getElementById(s.id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SiteNav />

      {/* Sticky nav */}
      <div className="sticky top-[57px] z-40 bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex gap-2 py-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
            {SECTIONS.map(s => (
              <button key={s.id} onClick={() => scrollTo(s.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap flex-shrink-0 transition-all ${activeSection === s.id ? 'bg-orange-500 text-white' : 'text-gray-500 hover:bg-gray-100'}`}>
                <s.icon className="w-3.5 h-3.5" />
                {s.title.split(' & ')[0].split(' ').slice(0, 2).join(' ')}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Hero */}
      <div style={{ background: 'linear-gradient(135deg, #0A0E1A 0%, #1B2D4F 50%, #E86C3D 100%)' }} className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
            Admin Guide:<br /><span className="text-amber-300">Run Your Program</span>
          </h1>
          <p className="text-lg text-white/70 max-w-lg mx-auto">Everything the organization admin needs to manage users, content, events, assessments, and reports.</p>
        </div>
      </div>

      {/* Sections */}
      <div className="max-w-4xl mx-auto px-4 py-12 space-y-16">
        {SECTIONS.map((section) => (
          <section key={section.id} id={section.id}>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${section.color}15` }}>
                  <section.icon className="w-6 h-6" style={{ color: section.color }} />
                </div>
                <div>
                  <h2 className="text-2xl font-black text-gray-900">{section.title}</h2>
                  <p className="text-sm text-gray-500">{section.description}</p>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed mb-5">{section.content}</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {section.features.map((f, fi) => (
                  <motion.div key={fi} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: fi * 0.1 }}
                    className="flex items-start gap-2 p-3 rounded-xl bg-gray-50">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: section.color }} />
                    <p className="text-sm text-gray-700">{f}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </section>
        ))}
      </div>

      <div className="py-16 px-4" style={{ background: 'linear-gradient(135deg, #0A0E1A 0%, #1B2D4F 100%)' }}>
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-black text-white mb-4">Ready to Manage Your Program?</h2>
          <p className="text-white/60 mb-6">Log in and start setting up. Everything is ready for you.</p>
          <Button onClick={() => navigate('/login')} className="rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-5 text-lg">
            Sign In <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
};

export default AdminTutorialPage;
